import React from 'react';
import { Dimensions, Text, View } from 'react-native';
import Svg, { Circle, G, Path, Text as SvgText } from 'react-native-svg';

import { useThemeColor } from '@/hooks/useThemeColor';

interface PieChartData {
  label: string;
  shortLabel?: string;
  value: number;
  color: string;
  percentage: number;
}

interface PieChartProps {
  data: PieChartData[];
  size?: number;
  showLegend?: boolean;
  showPercentages?: boolean;
}

export default function PieChart({ 
  data, 
  size = 200, 
  showLegend = true, 
  showPercentages = true 
}: PieChartProps) {
  const radius = (size - 40) / 2;
  const center = size / 2;
  let cumulativeAngle = 0;
  
  // Theme-aware colors
  const textColor = useThemeColor({}, 'text');
  
  // Check if screen is narrow (less than 400px width)
  const screenWidth = Dimensions.get('window').width;
  const isNarrowScreen = screenWidth < 400;

  // Calculate total value
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Generate SVG paths for pie slices
  const createSlice = (value: number, color: string, label: string) => {
    const angle = (value / total) * 360;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;
    
    cumulativeAngle += angle;

    // Convert angles to radians
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    // Calculate start and end points
    const x1 = center + radius * Math.cos(startAngleRad);
    const y1 = center + radius * Math.sin(startAngleRad);
    const x2 = center + radius * Math.cos(endAngleRad);
    const y2 = center + radius * Math.sin(endAngleRad);

    // Large arc flag
    const largeArcFlag = angle > 180 ? 1 : 0;

    // Create path
    const pathData = [
      `M ${center} ${center}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      'Z'
    ].join(' ');

    return (
      <Path
        key={label}
        d={pathData}
        fill={color}
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  if (total === 0) {
    return (
      <View style={{ alignItems: 'center', padding: 20 }}>
        <View style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: '#f0f0f0',
          justifyContent: 'center',
          alignItems: 'center',
          borderWidth: 2,
          borderColor: '#ddd',
          borderStyle: 'dashed'
        }}>
          <Text style={{ color: '#666', fontSize: 14, textAlign: 'center' }}>
            No data available
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={{ 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: 20,
      width: '100%'
    }}>
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <Svg width={size} height={size}>
        <G>
          {data.map((item) => createSlice(item.value, item.color, item.label))}
        </G>
        
        {/* Center circle for donut effect */}
        <Circle
          cx={center}
          cy={center}
          r={radius * 0.4}
          fill="white"
          stroke="#ddd"
          strokeWidth={1}
        />
        
        {/* Center text */}
        <SvgText
          x={center}
          y={center - 5}
          fontSize="14"
          fontWeight="bold"
          fill="#333"
          textAnchor="middle"
        >
          Total
        </SvgText>
        <SvgText
          x={center}
          y={center + 10}
          fontSize="16"
          fontWeight="bold"
          fill="#007AFF"
          textAnchor="middle"
        >
          {total}
        </SvgText>
      </Svg>

        {/* Legend */}
        {showLegend && (
          <View style={{ 
            marginLeft: 20, 
            justifyContent: 'center'
          }}>
          {data.map((item, index) => (
            <View key={index} style={{ 
              flexDirection: 'row', 
              alignItems: 'center', 
              marginVertical: 6,
              justifyContent: 'flex-start'
            }}>
              <View style={{
                width: 12,
                height: 12,
                backgroundColor: item.color,
                borderRadius: 2,
                marginRight: 8
              }} />
              <Text style={{ fontSize: 12, color: textColor }}>
                {isNarrowScreen && item.shortLabel ? item.shortLabel : item.label}
              </Text>
              {showPercentages && (
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#007AFF', marginLeft: 6 }}>
                    {Math.round(item.percentage)}%
                  </Text>
                )}
            </View>
          ))}
        </View>
        )}
      </View>
    </View>
  );
}
